import { createAsyncAction } from 'async-selector-kit'
import { API_PATH_TRANSACTIONS_CREATE } from '../../../../config'
import { onIsLoading } from '../../../../utils/onLoading'
import { Profile } from '../../types'
import { getApiConfig, getLocalServerURL } from '../../../../utils/getApiConfig'
import {
  getAllRows,
  getSelectedIndividualUnits,
} from '../../../SelectUnits/selectors'
import { getUnitProrateTotal } from '../../../Summary/hooks'
import { RootState } from '../../../../store'
import { executeCreateSubscription } from '../subscriptions/create'
import { getUnitsIndividual, getUnitsMaster } from '../../../Units/selectors'
import { onError } from '../../../../utils/onError'
import { actions as selectUnitsActions } from '../../../SelectUnits/reducer'

interface TransactionCreateBody {
  customerProfileId: number
  customerPaymentProfileId: number
  lastName: string
  unitNumber: string
  id: string
  size: string
  description: string
  total: string
  isClimate: boolean
  isCovered: boolean
}

export type StoreLike = { getState: () => RootState; dispatch: (action: unknown) => void }

/**
 * Core transaction create logic. Exported for testing (success, error, empty selection).
 */
export async function executeCreateTransaction(
  store: StoreLike,
  profile: Profile
): Promise<void> {
  const PATH = getLocalServerURL(API_PATH_TRANSACTIONS_CREATE)
  onIsLoading(true)
  try {
    const selectedIndividualUnits = getSelectedIndividualUnits(store.getState() as RootState)
    const masterUnits = getUnitsMaster(store.getState() as RootState)
    const individualUnits = getUnitsIndividual(store.getState() as RootState)
    const allRows = getAllRows(store.getState() as RootState)

    const transactionPromises = selectedIndividualUnits.map(async (x) => {
      const unit = allRows.find((y) => y.id === x.id)
      const iu = individualUnits.find((u) => u.unitNumber === x.unitNumber)
      const mu = masterUnits.find((u) => u.id === iu?.masterId)

      if (unit && mu != null) {
        const prorateTotal = getUnitProrateTotal(Number(mu.price))
        const model: TransactionCreateBody = {
          customerProfileId: profile.customerProfileId,
          customerPaymentProfileId: profile.customerPaymentProfileId,
          lastName: profile.lastName,
          id: unit.id,
          size: unit.size,
          isClimate: unit.isClimate,
          isCovered: Boolean(unit.isCovered),
          total: prorateTotal.toString(),
          unitNumber: unit.unitNumber,
          description: `${unit.size} / ${unit.unitNumber}`,
        }

        const config = await getApiConfig({ body: model })
        if (config != null) {
          const response = await fetch(PATH, config)
          if (!response.ok) {
            store.dispatch(selectUnitsActions.onResetState())
            throw new Error('Failed to create transaction')
          }

          const data = await response.json()
          if (data?.messages?.resultCode !== 'Ok') {
            store.dispatch(selectUnitsActions.onResetState())
            const error = data?.messages?.message?.[0]?.text || 'Unknown error'
            throw new Error(error)
          }
        }
      }
    })

    await Promise.all(transactionPromises)
    await executeCreateSubscription(store, profile)
  } catch (error) {
    onError(error instanceof Error ? error.message : 'An unexpected error occurred')
  } finally {
    onIsLoading(false)
  }
}

export const [createTransaction, _, createTransactionError] = createAsyncAction(
  {
    id: 'create-transaction',
    async: (store) => async (profile: Profile) => {
      await executeCreateTransaction(store, profile)
    },
  },
  []
)
